import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { SocietyAdminRow } from '../models/society-admin'

export const SOCIETIES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Sociedad',
  companyName: 'Empresa',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function SocietyActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activa' : 'Inactiva'}
    </span>
  )
}

export const societiesAdminColumns: ColumnDef<SocietyAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[260px]" column={column} name="Sociedad" />
    ),
    cell: ({ row }) => (
      <span className="block max-w-[24rem] truncate font-medium" title={row.original.name}>
        {row.original.name}
      </span>
    )
  },
  {
    accessorKey: 'companyName',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Empresa" />
    ),
    cell: ({ row }) =>
      row.original.companyName ? (
        <span className="block max-w-[18rem] truncate" title={row.original.companyName}>
          {row.original.companyName}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <SocietyActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
